import { useCallback, useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { text } from '../constants/translations';

function useGroups(userRole, language) {
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;

    const [groups, setGroups] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [createdGroups, setCreatedGroups] = useState([]);

    const normalizeGroupResponse = (data) => {
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.data)) return data.data;
        if (data && Array.isArray(data.groups)) return data.groups;
        return [];
    };
    // Starts true (not false) so the page-level skeleton is guaranteed to be
    // visible on first paint instead of racing the mount effect that flips it.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const fetchMemberOfGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/member-of-groups`, { method: "GET" });
            const data = await res.json();
            if (!res.ok) throw new Error('Failed to fetch member groups');
            setGroups(normalizeGroupResponse(data));
        } catch (err) {
            console.error('Error fetching my groups:', err);
            setError("Failed to load groups");
        } finally {
            setLoading(false);
        }
    };

    const fetchAllGroups = async () => {
        try {
            const res = await apiFetch(`${apiBaseUrl}/api/groups/`, { method: "GET" });
            const data = await res.json();
            if (!res.ok) throw new Error('Failed to fetch all groups');
            setAllGroups(normalizeGroupResponse(data));
        } catch (err) {
            console.error('Error fetching all groups:', err);
        }
    };

    const fetchCreatedGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/created-groups`, { method: "GET" });
            const data = await res.json();
            if (!res.ok) throw new Error('Failed to fetch created groups');
            setCreatedGroups(normalizeGroupResponse(data));
        } catch (err) {
            console.error('Error fetching created groups:', err);
            setError("Failed to load created groups");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async (newGroupName, onSuccess) => {
        if (userRole !== "admin" && userRole !== "manager") {
            setError(text[language].unauthorized);
            return;
        }

        if (!newGroupName.trim()) {
            setError(text[language].groupNameIsRequred);
            return;
        }

        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ groupName: newGroupName })
            });

            if (!res.ok) throw new Error("Failed to create group");

            setSuccess(text[language].groupCreated);
            fetchMemberOfGroups();
            if (userRole === "admin" || userRole === "manager") {
                fetchAllGroups();
            }
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(text[language].failedToCreate);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignEmployees = async (selectedGroup, selectedEmployeeIds, onSuccess) => {
        if (userRole !== "admin" && userRole !== "manager") {
            setError(text[language].unauthorized);
            return;
        }

        if (!selectedGroup || selectedEmployeeIds.length === 0) {
            setError(text[language].atLeastOneEmployee);
            return;
        }

        try {
            setLoading(true);
            // Infinity Codes are opaque strings (e.g. "ACME-0001"). parseInt turned
            // those into NaN, which JSON.stringify sends as null, so the backend
            // rejected the whole request. Send trimmed strings, deduplicated.
            const empIds = [...new Set(
                selectedEmployeeIds
                    .map(id => String(id ?? "").trim())
                    .filter(Boolean)
            )];

            const res = await apiFetch(`${apiBaseUrl}/api/groups/${selectedGroup.groupId}/members`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ empIds })
            });

            if (!res.ok) throw new Error("Failed to assign employees");

            setSuccess(text[language].employeesAssigned);
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(text[language].failedToAssign);
        } finally {
            setLoading(false);
        }
    };

    // GroupDetailsModal keys its data-loading effect on this function
    // reference (`useEffect(..., [group, fetchGroupMembers])`). Without
    // useCallback this was a brand-new function every render, and calling it
    // triggers setLoading — a state update on this hook, which re-renders
    // the page, which recreates this function, which re-triggers that
    // effect... an infinite fetch loop that hammered the API and the console
    // until the rate limiter stepped in. Stable deps here keep the reference
    // fixed across renders so the effect only fires when the group changes.
    const fetchGroupMembers = useCallback(async (groupId) => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/${groupId}/members`, { method: "GET" });
            const data = await res.json();
            if (!res.ok) throw new Error("Failed to fetch group members");
            return data.data || [];
        } catch (err) {
            console.error("Error fetching group members:", err);
            setError(text[language].failedToLoad || "Failed to load group members");
            return [];
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl, language]);

    return {
        groups,
        allGroups,
        createdGroups,
        loading,
        error,
        success,
        setError,
        setSuccess,
        fetchMemberOfGroups,
        fetchAllGroups,
        fetchCreatedGroups,
        handleCreateGroup,
        handleAssignEmployees,
        fetchGroupMembers,
    };
}

export default useGroups;